import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Image as ImageIcon, Video, FileType2, Upload, Loader2 } from "lucide-react";

/**
 * Drag-and-drop zone para upload de mídia de header de template.
 * Suporta IMAGE (jpg/png), VIDEO (mp4/3gpp) e DOCUMENT (pdf), até 5MB.
 */
export function MediaUploadZone({
  type,
  value,
  onUpload,
  uploading,
}: {
  type: "IMAGE" | "VIDEO" | "DOCUMENT";
  value: string;
  onUpload: (file: File) => void;
  uploading: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputId = `media-${type}`;

  const accept =
    type === "IMAGE" ? "image/jpeg,image/png" :
    type === "VIDEO" ? "video/mp4,video/3gpp" :
    "application/pdf";

  const sizeHint =
    type === "IMAGE" ? "JPG ou PNG, máx 5MB" :
    type === "VIDEO" ? "MP4 ou 3GPP, máx 5MB" :
    "PDF, máx 5MB";

  const Icon = type === "IMAGE" ? ImageIcon : type === "VIDEO" ? Video : FileType2;

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file);
  };

  return (
    <div className="space-y-2">
      <input
        type="file" id={inputId} className="hidden" accept={accept}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
      />

      {value && type === "IMAGE" ? (
        <div className="relative group rounded-lg overflow-hidden border-2 border-dashed border-emerald-300 bg-emerald-50/30">
          <img src={value} alt="Preview" className="w-full max-h-64 object-contain" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => document.getElementById(inputId)?.click()} disabled={uploading}>
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Trocar
            </Button>
          </div>
        </div>
      ) : value ? (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/30 p-3 flex items-center gap-3">
          <Icon className="h-8 w-8 text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{value.split("/").pop()}</p>
            <p className="text-xs text-muted-foreground">Mídia carregada</p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => document.getElementById(inputId)?.click()} disabled={uploading}>
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Trocar
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => document.getElementById(inputId)?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          disabled={uploading}
          className={`w-full rounded-lg border-2 border-dashed transition-all p-8 flex flex-col items-center justify-center gap-2 cursor-pointer
            ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-muted-foreground/30 hover:border-emerald-400 hover:bg-emerald-50/40"}`}
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
          ) : (
            <Icon className="h-8 w-8 text-muted-foreground" />
          )}
          <p className="text-sm font-medium">
            {uploading ? "Enviando..." : "Clique ou arraste o arquivo aqui"}
          </p>
          <p className="text-xs text-muted-foreground">{sizeHint}</p>
        </button>
      )}
    </div>
  );
}
